import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

export const TABLE_ZOOM_MIN = 0.5;
export const TABLE_ZOOM_MAX = 1.5;
export const TABLE_ZOOM_DEFAULT = 1;
export const TABLE_ZOOM_PRESETS: readonly number[] = [0.5, 0.75, 1, 1.25, 1.5];

export type TableZoomLabels = {
    readonly group: string;
    readonly reset: string;
    readonly zoomIn: string;
    readonly zoomOut: string;
};

export type TableZoomUpdate = number | ((current: number) => number);

export type TableZoomContextValue = {
    readonly enabled: boolean;
    readonly labels: TableZoomLabels;
    // Accepts an updater so continuous gestures compound on the latest level, not a stale render.
    readonly setZoom: (update: TableZoomUpdate) => void;
    readonly zoom: number;
};

const DEFAULT_LABELS: TableZoomLabels = {
    group: 'Table zoom',
    reset: 'Reset zoom',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
};

// The ui package has no lodash dependency, so the inert setter is spelled out here.
const ignoreZoomUpdate = (_update: TableZoomUpdate): void => undefined;

const DISABLED: TableZoomContextValue = {
    enabled: false,
    labels: DEFAULT_LABELS,
    setZoom: ignoreZoomUpdate,
    zoom: TABLE_ZOOM_DEFAULT,
};

type TableZoomPolicy = Omit<TableZoomContextValue, 'enabled'>;

const TableZoomPolicyContext = createContext<TableZoomPolicy | null>(null);

const TableZoomContext = createContext<TableZoomContextValue>(DISABLED);

export const clampTableZoom = (zoom: number): number =>
    Math.min(TABLE_ZOOM_MAX, Math.max(TABLE_ZOOM_MIN, Number.isFinite(zoom) ? zoom : TABLE_ZOOM_DEFAULT));

// Buttons snap to the nearest preset in the requested direction; wheel and pinch stay continuous.
export const stepTableZoom = (zoom: number, direction: -1 | 1): number => {
    const presets = direction > 0 ? TABLE_ZOOM_PRESETS : [...TABLE_ZOOM_PRESETS].reverse();
    const next = presets.find((preset) => (direction > 0 ? preset > zoom + 0.001 : preset < zoom - 0.001));

    return next ?? zoom;
};

export const useTableZoom = (): TableZoomContextValue => useContext(TableZoomContext);

type TableZoomProviderProps = PropsWithChildren<{
    readonly labels?: TableZoomLabels;
    readonly onZoomChange: (update: TableZoomUpdate) => void;
    readonly zoom: number;
}>;

export const TableZoomProvider: FunctionComponent<TableZoomProviderProps> = ({
    children,
    labels = DEFAULT_LABELS,
    onZoomChange,
    zoom,
}) => {
    const policy = useMemo<TableZoomPolicy>(
        () => ({ labels, setZoom: onZoomChange, zoom: clampTableZoom(zoom) }),
        [labels, onZoomChange, zoom],
    );

    return <TableZoomPolicyContext.Provider value={policy}>{children}</TableZoomPolicyContext.Provider>;
};

type TableZoomScopeProps = PropsWithChildren<{
    readonly enabled: boolean;
}>;

// The only place that enables zoom, so a grid, control, or gesture outside an enabled scope stays at app size.
export const TableZoomScope: FunctionComponent<TableZoomScopeProps> = ({ children, enabled }) => {
    const policy = useContext(TableZoomPolicyContext);
    const value = useMemo<TableZoomContextValue>(
        () => (enabled && policy !== null ? { enabled: true, ...policy } : DISABLED),
        [enabled, policy],
    );

    return <TableZoomContext.Provider value={value}>{children}</TableZoomContext.Provider>;
};
